import * as React from "react";
import { useState, useContext, useCallback } from "react";
import { MyNumberInputTemplateProps } from "@graffer-inc/form-generator";
import {
  MyNumberInputElement,
  MyNumberRawValue,
  RefkeyValueType,
} from "@graffer-inc/form-schema";
import { MyNumberModifyInput } from "./mynumber-modify-input";
import { reportAsyncError } from "@graffer-inc/smart-apply";
import { BaseTypeLiterals } from "../../../../application/domain/base/base-type";
import { ModifyingMyNumber } from "../../../../application/domain/form-model/modify-mynumber-exchanger";
import {
  FormContext,
  useMyNumberRefKeyExchanger,
} from "@graffer-inc/form-generator";

type ViewInputStateLiterals = "NO_VALUE" | "DECRYPTED" | "REFKEY" | "MODIFIED";

type Matcher<R> = BaseTypeLiterals<ViewInputStateLiterals, R>;

function matchState<T>(
  value: string | undefined,
  rawValue: MyNumberRawValue | undefined,
  decrypted: string | undefined,
  modified: string | undefined,
  matcher: Matcher<T>,
) {
  if (modified !== undefined) {
    return matcher["MODIFIED"];
  }
  if (decrypted !== undefined) {
    return matcher["DECRYPTED"];
  }
  if (rawValue === undefined || value === undefined) {
    return matcher["NO_VALUE"];
  }
  return matcher["REFKEY"];
}

export const MyNumberModifyConfirmInput: React.FC<
  MyNumberInputTemplateProps
> = ({ config, value, error, touched, setValue, ...props }) => {
  const {
    confirmContext: { editIds },
  } = useContext(FormContext);
  const isEditing = editIds?.includes(config.formFieldId);

  const { refkeyToValue } = useMyNumberRefKeyExchanger();
  const rawValue =
    value !== undefined && value !== ""
      ? (refkeyToValue(value) as ModifyingMyNumber)
      : undefined;
  const [decrypted, setDecrypted] = useState<string | undefined>();

  const requestDecrypt = useCallback(async () => {
    try {
      const myNumber = await (rawValue as any).requestDecrypt(rawValue?.value);
      setDecrypted(myNumber);
    } catch (e) {
      reportAsyncError(e);
    }
  }, [setDecrypted, rawValue]);
  const modified = rawValue?.myNumber;

  if (isEditing || (touched && !!error)) {
    return (
      <>
        <OriginalValueView config={config} setValue={setValue} />
        <MyNumberModifyInput
          config={config}
          value={value}
          error={error}
          touched={touched}
          setValue={setValue}
          {...props}
          disabled={false}
        />
      </>
    );
  }

  return (
    <p data-elem-id={config.formFieldId}>
      {matchState(value, rawValue, decrypted, modified, {
        DECRYPTED: <>{decrypted}</>,
        MODIFIED: <>{modified}</>,
        NO_VALUE: null,
        REFKEY: (
          <>
            ************{" "}
            <span className="icon c-hoverable" onClick={requestDecrypt}>
              <i className="fas fa-eye-slash" />
            </span>
          </>
        ),
      })}
    </p>
  );
};

export const OriginalValueView: React.FC<{
  config: MyNumberInputElement;
  setValue: (v: RefkeyValueType) => void;
}> = ({ config, setValue }) => {
  const {
    confirmContext: { onEdit },
    modificationContext: { detector },
  } = useContext(FormContext);

  // is not modifying
  if (detector === undefined) {
    return null;
  }

  const original = detector.getOriginalValue(config);
  const onReset = () => {
    setValue(original as RefkeyValueType);
    onEdit && onEdit(config, false, false);
  };

  const label = detector.getOriginalLabel(config);

  return (
    <div className="columns is-mobile">
      <div className="column">
        {label || <span className="tag is-small">入力なし</span>}
      </div>
      {detector.checkInput(config) && (
        <div className="column is-narrow">
          <button
            className="is-size-6"
            style={{
              border: "none",
              padding: "0 !important",
              color: "#069",
              textDecoration: "underline",
              cursor: "pointer",
              backgroundColor: "transparent",
            }}
            onClick={onReset}
          >
            元に戻す
          </button>
        </div>
      )}
    </div>
  );
};
